import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import ContentForm from './ContentForm';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [contents, setContents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regeneratingTTS, setRegeneratingTTS] = useState(new Set());
  const navigate = useNavigate();

  const fetchContents = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/contents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContents(response.data);
    } catch (error) {
      console.error('컨텐츠 조회 실패:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);



  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchContents();
  }, [navigate, fetchContents]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleClearSavedPassword = () => {
    if (window.confirm('저장된 로그인 정보를 삭제하시겠습니까?')) {
      localStorage.removeItem('adminUsername');
      localStorage.removeItem('adminPassword');
      alert('저장된 로그인 정보가 삭제되었습니다.');
    }
  };

  const handleRegenerateTTS = async (contentId) => {
    try {
      // 진행 상태 시작
      setRegeneratingTTS(prev => new Set([...prev, contentId]));

      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/contents/${contentId}/regenerate-tts`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message);
      // TTS 재생성 후 컨텐츠 목록 새로고침
      fetchContents();
    } catch (error) {
      console.error('TTS 재생성 실패:', error);
      alert('TTS 재생성에 실패했습니다.');
    } finally {
      // 진행 상태 종료
      setRegeneratingTTS(prev => {
        const newSet = new Set(prev);
        newSet.delete(contentId);
        return newSet;
      });
    }
  };

  const handleCopyHtmlUrl = async (contentId, accessKey) => {
    const htmlUrl = `https://shortseng.soocoolkim.workers.dev/data/${contentId}/${accessKey}.html`;

    try {
      await navigator.clipboard.writeText(htmlUrl);
      alert('HTML 파일 주소가 클립보드에 복사되었습니다!');
    } catch (error) {
      // 클립보드 API가 지원되지 않는 경우 fallback
      const textArea = document.createElement('textarea');
      textArea.value = htmlUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('HTML 파일 주소가 클립보드에 복사되었습니다!');
    }
  };

  const handlePublishContent = async (contentId, contentTitle) => {
    if (!window.confirm(`"${contentTitle}" 컨텐츠를 express로 발행하시겠습니까?\n\n이 작업은 JSON, HTML, MP3 파일을 express/public 폴더로 복사합니다.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/contents/${contentId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(response.data.message);
    } catch (error) {
      console.error('컨텐츠 발행 실패:', error);
      alert(error.response?.data?.error || '컨텐츠 발행에 실패했습니다.');
    }
  };

  const handleDeleteContent = async (contentId, contentTitle) => {
    if (!window.confirm(`"${contentTitle}" 컨텐츠를 정말 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없으며, 관련된 모든 오디오 파일도 함께 삭제됩니다.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete(
        `${API_BASE_URL}/api/admin/contents/${contentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(response.data.message);
      fetchContents(); // 목록 새로고침
    } catch (error) {
      console.error('컨텐츠 삭제 실패:', error);
      alert(error.response?.data?.error || '컨텐츠 삭제에 실패했습니다.');
    }
  };

  const handleEditContent = async (contentId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/contents/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingContent(response.data);
      setShowForm(true);
    } catch (error) {
      console.error('컨텐츠 조회 실패:', error);
      alert('컨텐츠 정보를 불러올 수 없습니다.');
    }
  };

  const handleContentCreated = () => {
    setShowForm(false);
    setEditingContent(null);
    fetchContents();
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingContent(null);
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>관리자 대시보드</h1>
        <div className="header-actions">
          <button onClick={() => { setEditingContent(null); setShowForm(true); }} className="btn-primary">
            새 컨텐츠 추가
          </button>
          <button onClick={handleClearSavedPassword} className="btn-secondary">
            저장된 비밀번호 삭제
          </button>
          <button onClick={handleLogout} className="btn-secondary">
            로그아웃
          </button>
        </div>
      </header>

      {showForm && (
        <ContentForm
          onClose={handleCloseForm}
          onContentCreated={handleContentCreated}
          editContent={editingContent}
        />
      )}

      <div className="contents-list">
        <div className="contents-header">
          <h2>컨텐츠 목록 (총 {contents.length}개)</h2>
        </div>
        {contents.length === 0 ? (
          <p>등록된 컨텐츠가 없습니다.</p>
        ) : (
          <div className="contents-grid">
            {contents.map(content => (
              <div key={content.idx} className="content-card">
                <h3>{content.title}</h3>
                <p>등록일: {new Date(content.created_at).toLocaleDateString()}</p>
                <p>목소리: {content.voice_type === 'male' ? '남성' : '여성'}</p>
                <div className="content-actions">
                  <a
                    href={content.viewerUrl || `/viewer/${content.idx}/${content.access_key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-view"
                  >
                    뷰어에서 보기
                  </a>
                  <a
                    href={`${content.viewerUrl || `/viewer/${content.idx}/${content.access_key}`}?admin=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-admin-view"
                  >
                    관리자 모드로 보기
                  </a>
                  <div className="viewer-url">
                    <small>URL: {window.location.origin}{content.viewerUrl || `/viewer/${content.idx}/${content.access_key}`}</small>
                  </div>
                  <button
                    onClick={() => handleCopyHtmlUrl(content.idx, content.access_key)}
                    className="btn-copy"
                  >
                    HTML 주소 복사
                  </button>
                  <button
                    onClick={() => handlePublishContent(content.idx, content.title)}
                    className="btn-publish"
                  >
                    📤 Publish
                  </button>
                  <button
                    onClick={() => handleEditContent(content.idx)}
                    className="btn-edit"
                  >
                    수정
                  </button>
                  <button
                    onClick={() => handleRegenerateTTS(content.idx)}
                    className={`btn-tts ${regeneratingTTS.has(content.idx) ? 'regenerating' : ''}`}
                    disabled={regeneratingTTS.has(content.idx)}
                  >
                    {regeneratingTTS.has(content.idx) ? '재생성 중...' : 'TTS 재생성'}
                  </button>
                  <button
                    onClick={() => handleDeleteContent(content.idx, content.title)}
                    className="btn-delete"
                  >
                    삭제
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;